import moment from 'moment';
import { push } from 'connected-react-router';
import { change, getFormValues } from 'redux-form';
import { routes } from 'app/routes/route-list';
import {
  REVERSE_ADVISOR_STOP_GATE,
  REVERSE_CITIZEN_AND_MARRIAGE,
  SUMMARY_SECTION,
} from 'app/models/sections/constants';
import { getFormType } from 'app/routes/helpers';
import { ReverseMortgageCalcsRequest, ReverseMortgageType } from 'app/api/rates/types';
import { REVERSE_MORTGAGE_HIGH_TECH_PAYMENT_OPTIONS } from 'app/models/options/options';
import { hasCoBorrower, isUsingReverseToPurchase } from 'app/models/fields/conditionals';
import {
  ajaxPostShortForm,
  getNextSection,
  goToSection,
  referToLoanOfficer,
} from 'app/actions/form/actions';
import { FieldNames } from 'app/models/fields/names';
import { fetchReverseMortgageCalcs } from 'app/api/rates';
import { getAuditMetadata, makeActionCreator } from 'app/util/actions';
import { Type } from 'app/actions/form/reverse-mortgage/types';
import { UniversalLoanSource, GeneralReferralCode, ReverseMortgageLoanType, ReverseSummaryPdfRequest, ReverseSummaryType } from '@lenderful/domain';
import { getLoanOfficer } from 'app/reducers/loan-officer';
import { reverseSummaryTypeSelector } from 'app/reducers/app-config';
import {
  mapUniversalLoanRequest,
  patchUniversalLoanApplication,
  postUniversalLoanApplication,
} from 'app/api/loan-application/universal/loan';
import { logger } from 'app/util/logger';
import { hasEitherSsn } from '../../../components/FormFields/Ssn/CollectSsnBase';
import { postEmailReverseSummary } from 'app/api/reverse-summary';
import { Dispatch } from 'redux';
import { getDownPayment, getEstimatedHomeValue, getLineOfCredit, getLoanTerm, getMortgageBalance, getTenurePayment, getTermPayment } from 'app/api/loan-application/universal/reverse';
import { ajaxValidateBorrowerResidenceNoNavigate } from '../address-validation/actions';
import { YesOrNo } from '../../../models/options/enums';
import { mapBrokerLoanOfficer } from 'app/api/loan-application/universal/mappings';
import { getSlug } from 'app/util/headers';
import { isBadRequestError } from 'app/util/misc';

export const ajaxReverseMortgageCalcs        = makeActionCreator(Type.AJAX_REVERSE_MORTGAGE_CALCS, 'payload');
export const ajaxReverseMortgageCalcsSuccess = makeActionCreator(Type.AJAX_REVERSE_MORTGAGE_CALCS_SUCCESS, 'payload');
export const ajaxReverseMortgageCalcsFailure = makeActionCreator(Type.AJAX_REVERSE_MORTGAGE_CALCS_FAIL, 'payload');
export const ajaxEmailReverseSummary         = makeActionCreator(Type.AJAX_EMAIL_REVERSE_SUMMARY, 'payload');
export const ajaxEmailReverseSummaryFail     = makeActionCreator(Type.AJAX_EMAIL_REVERSE_SUMMARY_FAIL, 'payload');
export const ajaxEmailReverseSummarySuccess  = makeActionCreator(Type.AJAX_EMAIL_REVERSE_SUMMARY_SUCCESS, 'payload');

/**
 * @param {ReverseMortgageType} [reverseMortgageType] which program (HECM or High Tech) the borrower
 * chose to apply for. @todo map this into the application payload once the contract exists (POC).
 */
export function handleReverseMortgageLoanSelection(reverseMortgageType?: ReverseMortgageType) {
  return (dispatch) => {
    if (reverseMortgageType) {
      dispatch(change(UniversalLoanSource.REVERSE, FieldNames.reverseMortgageType, reverseMortgageType));
    }
    dispatch(change(UniversalLoanSource.REVERSE, FieldNames.hasReverseSummaryInfo, YesOrNo.YES));
    dispatch(push(`${routes.reverse}#${REVERSE_CITIZEN_AND_MARRIAGE}`));
  };
}

export function handleReverseMortgageAskForAdvisor() {
  return (dispatch) => {
    dispatch(push(`${routes.reverse}#${REVERSE_ADVISOR_STOP_GATE}`));
  };
}

export function handleAddressValidationAndReverseShortFormSubmit() {
  return async (dispatch, getState) => {
    const state = getState();
    const formName = getFormType(state.router.location.pathname);
    const formData = getFormValues(formName)(state);
    ajaxValidateBorrowerResidenceNoNavigate(formData);
    return dispatch(handleReverseShortFormSubmit());
  };
}

export function handleReverseShortFormSubmit() {
  return async (dispatch) => {
    try {
      await dispatch(calculateReverseMortgage());
      // post short form data
      await dispatch(ajaxPostShortForm());
      // navigate to summary
      return dispatch(goToSection(SUMMARY_SECTION));
    } catch (error) {
      // navigate to referral
      if (error.response.status === 400 && error.response?.data?.error?.code === 'STATE_NOT_SUPPORTED') {
        dispatch(referToLoanOfficer(GeneralReferralCode.STATE_NOT_SUPPORTED));
        return false;
      }
    }
  };
}

const isStateNotSupportedError = (error: any): boolean => {
  return error?.response?.status === 400 && error?.response?.data?.error?.code === 'STATE_NOT_SUPPORTED';
};

export function calculateReverseMortgage() {
  return async (dispatch, getState) => {

    const state = getState();
    const formName = getFormType(state.router.location.pathname);
    const values = getFormValues(formName)(state);
    const isHighTechEnabled = reverseSummaryTypeSelector(state) === ReverseSummaryType.HECM_AND_HIGH_TECH;

    const today = moment();

    const borrowerAgeInFiveMonths = values[FieldNames.age] ?
      values[FieldNames.age] :
      today.diff(moment(values[FieldNames.dateOfBirth]).add(5, 'M'), 'years');

    const coBorrowerAgeInFiveMonths = values[FieldNames.coBorrowerAge] ?
      values[FieldNames.coBorrowerAge] :
      today.diff(moment(values[FieldNames.coBorrowerDOB]).add(5, 'M'), 'years');

    const baseRequest: ReverseMortgageCalcsRequest = {
      ageOfYoungest      : hasCoBorrower(values) ? Math.min(borrowerAgeInFiveMonths, coBorrowerAgeInFiveMonths) : borrowerAgeInFiveMonths,
      estimatedHomeValue : getEstimatedHomeValue(values),
      expectedLumpSum    : values[FieldNames.expectedLumpSum],
      mortgageBalance    : getMortgageBalance(values),
      propertyState      : values[FieldNames.propertyState] ? values[FieldNames.propertyState] : values[FieldNames.livingPropertyState],
      term               : values[FieldNames.loanTerm] * 12,
    };

    const paymentOption = values[FieldNames.paymentOption] ?? REVERSE_MORTGAGE_HIGH_TECH_PAYMENT_OPTIONS[0].value;
    const highTechRequest: ReverseMortgageCalcsRequest = {
      ...baseRequest,
      type          : ReverseMortgageType.HIGH_TECH,
      paymentOption,
    };

    dispatch(ajaxReverseMortgageCalcs());
    try {
      // The two calcs succeed or fail independently: a failure on one side must not throw away the
      // other side's results, and the summary disables Apply Now for whichever side failed.
      const [hecmResult, highTechResult] = await Promise.allSettled([
        fetchReverseMortgageCalcs(baseRequest),
        isHighTechEnabled ? fetchReverseMortgageCalcs(highTechRequest) : Promise.resolve(undefined),
      ]);
      const hecmFailed     = hecmResult.status === 'rejected';
      const highTechFailed = highTechResult.status === 'rejected';

      [hecmResult, highTechResult].forEach((result) => {
        if (result.status === 'rejected') {
          logger.error('Error: Reverse Mortgage calcs', result.reason);
        }
      });

      const stateNotSupported = [hecmResult, highTechResult].find(
        (result) => result.status === 'rejected' && isStateNotSupportedError(result.reason),
      ) as PromiseRejectedResult | undefined;
      if (stateNotSupported) {
        // navigate to referral (the catch below records the failure)
        dispatch(referToLoanOfficer(GeneralReferralCode.STATE_NOT_SUPPORTED));
        throw stateNotSupported.reason;
      }

      if (hecmFailed && highTechFailed) {
        dispatch(ajaxReverseMortgageCalcsFailure());
        return;
      }
      dispatch(ajaxReverseMortgageCalcsSuccess({ hecmFailed, highTechFailed }));

      if (hecmResult.status === 'fulfilled') {
        const hecmResponse = hecmResult.value;
        dispatch(change(formName, FieldNames.initialMortgageInsurance, hecmResponse.initialMortgageInsurance));
        dispatch(change(formName, FieldNames.maxClaimAmount, hecmResponse.maxClaimAmount));
        dispatch(change(formName, FieldNames.maxLumpSum, hecmResponse.maxLumpSum));
        dispatch(change(formName, FieldNames.netPrincipalLimit, hecmResponse.netPrincipalLimit));
        dispatch(change(formName, FieldNames.tenurePayment, hecmResponse.tenurePayment));
        dispatch(change(formName, FieldNames.termPayment, hecmResponse.termPayment));
        dispatch(change(formName, FieldNames.totalUpfrontCosts, hecmResponse.totalUpfrontCosts));

        // Unlike paymentOption below, this isn't a debounce dependency, so echoing it back on every
        // calc is safe and won't re-trigger this action.
        dispatch(change(formName, FieldNames.reverseMortgageLoanType, values[FieldNames.reverseMortgageLoanType] ?? ReverseMortgageLoanType.LINE_OF_CREDIT));

        if (isUsingReverseToPurchase(values)) {
          const downPayment = getDownPayment(values[FieldNames.purchasePrice], hecmResponse.netPrincipalLimit, hecmResponse.totalUpfrontCosts);
          dispatch(change(formName, FieldNames.downDollar, Math.max(downPayment, 0)));
        }
      }

      if (isHighTechEnabled && highTechResult.status === 'fulfilled') {
        const highTechResponse = highTechResult.value;
        // Only seed the default when unset - paymentOption drives the debounce that calls this
        // action, so echoing it back every time would re-trigger an infinite calc loop.
        if (values[FieldNames.paymentOption] == null) {
          dispatch(change(formName, FieldNames.paymentOption, paymentOption));
        }
        dispatch(change(formName, FieldNames.ltv, highTechResponse.ltv));
        dispatch(change(formName, FieldNames.monthlyPayment, highTechResponse.monthlyPayment));
        dispatch(change(formName, FieldNames.maxLineOfCredit, highTechResponse.maxLineOfCredit));
        dispatch(change(formName, FieldNames.minLoanAmount, highTechResponse.minLoanAmount));
        dispatch(change(formName, FieldNames.maxLoanAmount, highTechResponse.maxLoanAmount));
      }
    } catch (error) {
      dispatch(ajaxReverseMortgageCalcsFailure());
      if (isStateNotSupportedError(error)) {
        throw error;
      }
      logger.error('Error: Reverse Mortgage calcs', error);
    }
  };
}

export function handleReverseFormSubmit() {
  return async (dispatch) => {
    // @todo add handleDeclarationFlagsWhitelist()
    try {
      await dispatch(handleReverseMortgageApplication());
    } catch (error) {
      return; // non-400 failure: stay on this page, error already logged above
    }
    return dispatch(getNextSection());
  };
}

/**
 * This method builds the new payload before submitting to the backend endpoint
 *
 * @param {FormName} formName
 * @param {FieldNames} fieldName
 * @param {string} value
 */
export const handleReverseMortgageApplication = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const formName = getFormType(state.router.location.pathname);
    const formData = getFormValues(formName)(state);
    const loanOfficer = getLoanOfficer(state);

    const universalLoanRequest = mapUniversalLoanRequest(formData, loanOfficer, formName);
    let auditMetaData;
    if (hasEitherSsn()) {
      auditMetaData = getAuditMetadata('Submit button');
    }
    if(formData[FieldNames.universalApplicationId]) {
      try {
        const response = await patchUniversalLoanApplication({ ...universalLoanRequest, auditMetaData });
        logger.debug('Patch Reverse Mortgage Response', response);
        return response;
      } catch (error) {
        logger.error('Error: Patch Reverse Mortgage', error);
        // A 400 means the backend rejected this application; swallow it here so the
        // caller advances to confirmation anyway. Any other failure re-throws so the
        // caller keeps the user on this page.
        if (!isBadRequestError(error)) {
          throw error;
        }
      }
    } else {
      try {
        const response = await postUniversalLoanApplication({ ...universalLoanRequest, auditMetaData });
        logger.debug('Post Reverse Mortgage Response', response);
        return response;
      } catch (error) {
        logger.error('Error: Post Reverse Mortgage', error);
        // A 400 means the backend rejected this application; swallow it here so the
        // caller advances to confirmation anyway. Any other failure re-throws so the
        // caller keeps the user on this page.
        if (!isBadRequestError(error)) {
          throw error;
        }
      }
    }
  };
};

export function handleEmailReverseMortgageSummary() {
  return (dispatch: Dispatch, getState) => {
    const state       = getState();
    const formName    = getFormType(state.router.location.pathname);
    const values      = getFormValues(formName)(state);
    const loanOfficer = getLoanOfficer(state);

    const brokerLoanOfficer       = mapBrokerLoanOfficer(values);
    const mortgageBalance         = getMortgageBalance(values);
    const tenurePayment           = getTenurePayment(values);
    const termPayment             = getTermPayment(values);
    const loanTerm                = getLoanTerm(values);
    const estimatedHomeValue      = getEstimatedHomeValue(values);
    const lineOfCreditAmount      = getLineOfCredit(values);
    const propertyAppraisedValue  = values[FieldNames.homeValue];
    const netPrincipalLimit       = values[FieldNames.netPrincipalLimit];
    const totalUpfrontCosts       = values[FieldNames.totalUpfrontCosts];
    const reverseMortgageLoanType = values[FieldNames.reverseMortgageLoanType];
    const purchasePrice           = values[FieldNames.purchasePrice];
    const expectedLumpSum         = values[FieldNames.expectedLumpSum];
    const downPayment             = getDownPayment(purchasePrice, netPrincipalLimit, totalUpfrontCosts);
    const slug                    = getSlug();

    const remainingEquity = propertyAppraisedValue - mortgageBalance - netPrincipalLimit - totalUpfrontCosts;

    const payload: ReverseSummaryPdfRequest = {
      brokerLoanOfficer,
      downPayment,
      email               : values[FieldNames.email],
      estimatedHomeValue,
      expectedLumpSum,
      firstName           : values[FieldNames.firstName],
      isPurchasingNewHome : isUsingReverseToPurchase(values),
      lastName            : values[FieldNames.lastName],
      lineOfCreditAmount,
      loanOfficerId       : loanOfficer.id,
      loanTerm,
      mortgageBalance,
      netPrincipalLimit,
      propertyStreet      : isUsingReverseToPurchase(values) ? values[FieldNames.propertyStreet] : values[FieldNames.livingPropertyStreet],
      propertyStreet2     : isUsingReverseToPurchase(values) ? values[FieldNames.propertyStreet2] : values[FieldNames.livingPropertyStreet2],
      propertyCity        : isUsingReverseToPurchase(values) ? values[FieldNames.propertyCity] : values[FieldNames.livingPropertyCity],
      propertyState       : isUsingReverseToPurchase(values) ? values[FieldNames.propertyState] : values[FieldNames.livingPropertyState],
      propertyZip         : isUsingReverseToPurchase(values) ? values[FieldNames.propertyZip] : values[FieldNames.livingPropertyZip],
      purchasePrice,
      remainingEquity,
      reverseMortgageLoanType,
      slug,
      tenurePayment,
      termPayment,
      totalUpfrontCosts,
    };
    dispatch({ type: Type.AJAX_EMAIL_REVERSE_SUMMARY, payload });
    return postEmailReverseSummary(payload).then(
      (response) => {
        dispatch(ajaxEmailReverseSummarySuccess(response));
      },
      (error) => {
        dispatch(ajaxEmailReverseSummaryFail(error));
      });
  };
}
